import { useEffect, useState } from "react";
import type { PropsWithChildren, ReactNode } from "react";
import { Grid, Typography, useMediaQuery } from "@mui/material";
import type { TypographyProps } from "@mui/material";
import BackButton from "./BackButton";
import type Competence from "constants/Competence";
import CompetenceChip from "./Typography/CompetenceChip";

interface PageTitleProps extends PropsWithChildren {
  noBackButton?: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  typographyProps?: TypographyProps;
  competences?: Array<Competence> | Competence;
}

function PageTitle({
  noBackButton = false,
  children,
  title,
  subtitle,
  icon,
  typographyProps,
  competences: originalCompetences,
}: Readonly<PageTitleProps>): ReactNode {
  const [competences, setCompetences] = useState<Array<Competence>>([]);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    if (originalCompetences != null) {
      setCompetences(
        Array.isArray(originalCompetences) ? originalCompetences : [originalCompetences]
      );
    } else {
      setCompetences([]);
    }
  }, [originalCompetences]);

  return (
    <Grid
      container
      size={12}
      sx={{
        alignItems: "center",
        py: 2,
        mt: -2,
        background: "white",
        position: "sticky",
        top: { xs: "54px", sm: "62px" },
        zIndex: 10,
        color: "text.primary",
      }}>
      {!noBackButton && <BackButton sx={{ ml: -2 }} />}
      {icon}
      <Typography
        component={Grid}
        size="grow"
        variant={isSmallScreen ? "h3" : "h2"}
        sx={{ ml: 2 }}
        {...typographyProps}>
        {title}
      </Typography>

      {(subtitle != null || competences?.length > 0) && (
        <Grid container spacing={1} size={12}>
          <Typography variant="caption" sx={{ fontSize: { xs: ".9rem", md: "1rem" }, ml: 3.5 }}>
            {subtitle}
          </Typography>
          {competences.map((competence) => (
            <CompetenceChip key={competence} competence={competence} />
          ))}
        </Grid>
      )}
      {children}
    </Grid>
  );
}

export default PageTitle;
